/**
 * Floating feedback button + panel.
 *
 * Renders nothing when the backend reports the feedback channel as unavailable,
 * so a misconfigured deployment never shows a button that cannot work.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Fab from '@mui/material/Fab';
import Box from '@mui/material/Box';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import { FeedbackPanel } from './FeedbackPanel';
import { useFeedbackConfig } from '../../hooks/useFeedbackConfig';

interface FeedbackWidgetProps {
  /** Active search query, sent along as context for the support agent. */
  searchQuery?: string;
}

export function FeedbackWidget({ searchQuery }: FeedbackWidgetProps) {
  const { t } = useTranslation('feedback');
  const config = useFeedbackConfig();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  if (!config.enabled) return null;

  return (
    <>
      <Fab
        ref={anchorRef}
        variant="extended"
        size="medium"
        color="default"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={t('fab')}
        aria-expanded={open}
        aria-haspopup="dialog"
        sx={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: (theme) => theme.zIndex.fab,
          textTransform: 'none',
          bgcolor: 'secondaryContainer',
          color: 'onSecondaryContainer',
          '&:hover': {
            bgcolor: 'secondaryContainer',
            filter: 'brightness(0.95)',
          },
        }}
      >
        <FeedbackOutlinedIcon fontSize="small" sx={{ mr: { xs: 0, sm: 1 } }} />
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
          {t('fab')}
        </Box>
      </Fab>

      <FeedbackPanel
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        feedbackConfig={config}
        searchQuery={searchQuery}
      />
    </>
  );
}
